// Gider Masraf Kartı — gider fişi kalemlerinin bağlanacağı kategorileri yönetir.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, ListPageHeader } from '../components/ui';
import DataTable from '../components/DataTable';
import { fmtAmount } from '../utils/numberFormat';

const fmt = (n) => fmtAmount(n);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '';

const PAY_LABELS = { cash: 'Kasa', bank: 'Banka', card: 'Kredi Kartı', cari: 'Cariye Borç', personnel: 'Personel Avansı' };
const DOC_LABELS = { gider_fisi: 'Gider Fişi', gider_pusulasi: 'Gider Pusulası', belgesiz_kkeg: 'KKEG' };

const emptyCategory = () => ({
  id: null, code: '', name: '', account_code: '770',
  is_kkeg: false, default_vat_rate: 20, sort_order: 0, is_active: true,
});

export default function ExpenseCategoriesPage({ onNavigate } = {}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState('info'); // 'info' | 'movements'
  const [movements, setMovements] = useState([]);
  const [movLoading, setMovLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/expense-vouchers/categories');
      if (r.data.success) setList(r.data.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openEdit = (c) => {
    setErr(null);
    setEditing({ ...c });
    setTab('info');
    setMovements([]);
  };

  const openAdd = () => {
    setErr(null);
    setEditing(emptyCategory());
    setTab('info');
    setMovements([]);
  };

  const loadMovements = async (categoryId) => {
    setMovLoading(true);
    try {
      const r = await api.get(`/expense-vouchers/categories/${categoryId}/transactions`);
      if (r.data.success) setMovements(r.data.data);
    } catch (e) {
      setMovements([]);
    } finally { setMovLoading(false); }
  };

  useEffect(() => {
    if (editing?.id && tab === 'movements') loadMovements(editing.id);
  }, [editing?.id, tab]);

  const save = async () => {
    if (!editing.name || !editing.account_code) {
      setErr('Ad ve hesap kodu zorunludur');
      return;
    }
    setBusy(true); setErr(null);
    try {
      const body = {
        code: editing.code || null,
        name: editing.name,
        account_code: editing.account_code,
        is_kkeg: editing.is_kkeg,
        default_vat_rate: editing.default_vat_rate != null ? Number(editing.default_vat_rate) : null,
        sort_order: editing.sort_order || 0,
        is_active: editing.is_active,
      };
      if (editing.id) {
        await api.put('/expense-vouchers/categories/' + editing.id, body);
      } else {
        await api.post('/expense-vouchers/categories', body);
      }
      await fetchList();
      setEditing(null);
      setMsg({ type: 'success', text: editing.id ? 'Kategori güncellendi' : 'Kategori eklendi' });
      setTimeout(() => setMsg(null), 1800);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!editing?.id) return;
    if (!window.confirm(`"${editing.name}" kategorisi silinecek. Emin misin?`)) return;
    setBusy(true); setErr(null);
    try {
      await api.delete('/expense-vouchers/categories/' + editing.id);
      await fetchList();
      setEditing(null);
      setMsg({ type: 'success', text: 'Kategori silindi' });
      setTimeout(() => setMsg(null), 1800);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally { setBusy(false); }
  };

  const seedDefaultCategories = async () => {
    if (!window.confirm('Standart gider kategorileri (akaryakıt, kira, elektrik vb. + KKEG kategorileri) yüklenecek. Devam edilsin mi?')) return;
    try {
      const r = await api.post('/expense-vouchers/categories/seed-defaults');
      if (r.data.success) {
        await fetchList();
        setMsg({ type: 'success', text: `${r.data.data.inserted} kategori yüklendi` });
        setTimeout(() => setMsg(null), 2000);
      }
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || e.message }); }
  };

  const filtered = list.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(s)
        || (c.code || '').toLowerCase().includes(s)
        || (c.account_code || '').toLowerCase().includes(s);
  });

  const totalsSum = filtered.reduce((acc, c) => acc + (parseFloat(c.total_amount) || 0), 0);
  const hasMovements = !!(editing?.id && editing?.movement_count > 0);

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Gider Masraf Kartı"
        count={filtered.length}
        unit="kategori"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Ad, kod, hesap ara..."
        onRefresh={fetchList}
        refreshing={loading}
        actions={
          <>
            {list.length === 0 && (
              <Button variant="ghost" size="sm" onClick={seedDefaultCategories}>📦 Varsayılanları Yükle</Button>
            )}
            <Button size="sm" onClick={openAdd}>+ Yeni Kategori</Button>
          </>
        }
      />

      {msg && (
        <div style={{
          padding: '10px 14px', margin: '12px 0', borderRadius: 8, fontSize: '0.85rem',
          background: msg.type === 'error' ? 'rgba(220,38,38,0.1)' : 'rgba(22,163,74,0.1)',
          color: msg.type === 'error' ? 'var(--danger)' : 'var(--success)',
          border: '1px solid ' + (msg.type === 'error' ? 'rgba(220,38,38,0.3)' : 'rgba(22,163,74,0.3)'),
        }}>{msg.text}</div>
      )}

      {list.length === 0 && !loading && (
        <div style={{
          padding: '12px 14px', margin: '12px 0', borderRadius: 8, fontSize: '0.85rem',
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
          color: 'var(--warning)',
        }}>
          <strong>Henüz kategori yok.</strong> Standart kategorileri (akaryakıt, kira, elektrik, KKEG vb.) tek tıkla yükleyin veya kendiniz tanımlayın.
        </div>
      )}

      {filtered.length > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 24, padding: '8px 14px',
          margin: '8px 0', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.82rem',
        }}>
          <span style={{ color: 'var(--text-tertiary)' }}>Toplam Gider:</span>
          <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--danger)' }}>{fmt(totalsSum)} TRY</span>
        </div>
      )}

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="expense-categories-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          data={filtered}
          emptyText="Kategori bulunamadı"
          rowKey="id"
          onRowClick={openEdit}
          columns={[
            { id: 'code', header: 'Kod', accessor: 'code', sortable: true, width: 100,
              cell: c => <span style={{ fontFamily: 'monospace', color: 'var(--text-tertiary)' }}>{c.code || '-'}</span> },
            { id: 'name', header: 'Ad', accessor: 'name', sortable: true,
              cell: c => <span style={{ fontWeight: 500 }}>{c.name}</span> },
            { id: 'account_code', header: 'TDHP Hesap', accessor: 'account_code', sortable: true, width: 110,
              cell: c => <span style={{ fontFamily: 'monospace' }}>{c.account_code}</span> },
            { id: 'default_vat_rate', header: 'KDV', accessor: 'default_vat_rate', sortable: true, width: 80,
              cell: c => c.default_vat_rate != null ? '%' + c.default_vat_rate : '-' },
            { id: 'is_kkeg', header: 'KKEG', accessor: 'is_kkeg', sortable: true, width: 80,
              cell: c => c.is_kkeg ? <span style={{ color: 'var(--warning)', fontWeight: 600 }}>⚠ KKEG</span> : '-' },
            { id: 'movement_count', header: 'Hareket', accessor: 'movement_count', sortable: true, width: 90,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: c => <span style={{ color: c.movement_count > 0 ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>{c.movement_count || 0}</span> },
            { id: 'total_amount', header: 'Toplam Tutar', accessor: 'total_amount', sortable: true, width: 140,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap' },
              cell: c => parseFloat(c.total_amount) > 0
                ? <span style={{ fontWeight: 700, color: 'var(--danger)' }}>{fmt(c.total_amount)}</span>
                : <span style={{ color: 'var(--text-tertiary)' }}>—</span> },
            { id: 'is_active', header: 'Durum', accessor: 'is_active', sortable: true, width: 80,
              cell: c => c.is_active
                ? <span style={{ color: 'var(--success)', fontSize: '0.78rem' }}>✓ Aktif</span>
                : <span style={{ color: 'var(--text-tertiary)', fontSize: '0.78rem' }}>— Pasif</span> },
          ]}
        />
      </div>

      {editing && (
        <Modal
          title={editing.id ? `Kategori: ${editing.name}` : 'Yeni Kategori'}
          subtitle={editing.id ? null : 'Her kategori bir TDHP hesap koduna ve KDV oranına bağlanır'}
          size="lg"
          onClose={() => setEditing(null)}
          closeOnOverlay={false}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {editing.id && (
                  <Button variant="danger" onClick={remove} loading={busy} disabled={hasMovements}
                          title={hasMovements ? 'Bu kategori hareket görmüş — silinemez. Önce pasifleştirin.' : 'Kategoriyi sil'}>
                    🗑 Sil
                  </Button>
                )}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>Vazgeç</Button>
                {tab === 'info' && <Button variant="primary" onClick={save} loading={busy}>💾 Kaydet</Button>}
              </div>
            </>
          }
        >
          {err && (
            <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8, fontSize: '0.85rem',
              background: 'rgba(220,38,38,0.1)', color: 'var(--danger)', border: '1px solid rgba(220,38,38,0.3)' }}>
              {err}
            </div>
          )}

          {editing.id && (
            <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-secondary)', marginBottom: 16 }}>
              <TabButton active={tab === 'info'} onClick={() => setTab('info')}>📋 Bilgi</TabButton>
              <TabButton active={tab === 'movements'} onClick={() => setTab('movements')}>
                💸 Hareketler {editing.movement_count > 0 ? `(${editing.movement_count})` : ''}
              </TabButton>
            </div>
          )}

          {tab === 'info' ? (
            <InfoForm editing={editing} setEditing={setEditing} />
          ) : (
            <MovementsTab movements={movements} loading={movLoading}
              onOpenVoucher={(id) => { if (onNavigate) { setEditing(null); onNavigate('expense-vouchers', id); } }}
            />
          )}
        </Modal>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer',
      borderBottom: '2px solid ' + (active ? 'var(--accent-primary)' : 'transparent'),
      color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
      fontWeight: active ? 600 : 400, fontSize: '0.88rem',
      marginBottom: -1,
    }}>{children}</button>
  );
}

function InfoForm({ editing, setEditing }) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
        <FormField label="Kod (opsiyonel)" hint="Kısa kod, örn: 770.10">
          <input value={editing.code} onChange={e => setEditing({ ...editing, code: e.target.value })} placeholder="örn: 770.10" />
        </FormField>
        <FormField label="Kategori Adı *" hint="Kullanıcının göreceği isim">
          <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="örn: Akaryakıt" autoFocus />
        </FormField>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <FormField label="TDHP Hesap Kodu *" hint="770/760/689 vb.">
          <input value={editing.account_code} onChange={e => setEditing({ ...editing, account_code: e.target.value })} placeholder="770" />
        </FormField>
        <FormField label="Varsayılan KDV %">
          <select value={editing.default_vat_rate ?? ''}
            onChange={e => setEditing({ ...editing, default_vat_rate: e.target.value === '' ? null : Number(e.target.value) })}>
            <option value="">— yok —</option>
            <option value="0">%0</option>
            <option value="1">%1</option>
            <option value="10">%10</option>
            <option value="20">%20</option>
          </select>
        </FormField>
        <FormField label="Sıra">
          <input type="number" value={editing.sort_order} onChange={e => setEditing({ ...editing, sort_order: Number(e.target.value) || 0 })} />
        </FormField>
      </div>
      <div style={{ display: 'flex', gap: 16, padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!editing.is_kkeg} onChange={e => setEditing({ ...editing, is_kkeg: e.target.checked })} />
          <span><strong>KKEG</strong> (Kanunen Kabul Edilmeyen Gider) — KDV indirilmez, kurumlar vergisi matrahına eklenir</span>
        </label>
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} />
          <span>Aktif</span>
        </label>
      </div>
      {editing.id && parseFloat(editing.total_amount) > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.85rem',
        }}>
          <span style={{ color: 'var(--text-tertiary)' }}>Bu kategoriyle toplam gider ({editing.movement_count} hareket):</span>
          <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--danger)' }}>{fmt(editing.total_amount)} TRY</span>
        </div>
      )}
    </div>
  );
}

function MovementsTab({ movements, loading, onOpenVoucher }) {
  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-tertiary)' }}>Yükleniyor…</div>;
  if (!movements.length) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-tertiary)' }}>Bu kategoriyle henüz gider kaydedilmemiş.</div>;

  const total = movements.filter(m => m.status !== 'cancelled').reduce((acc, m) => acc + (parseFloat(m.gross_amount) || 0), 0);

  return (
    <div>
      <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-primary)', zIndex: 1 }}>
            <tr style={{ borderBottom: '1px solid var(--border-secondary)', color: 'var(--text-tertiary)', fontSize: '0.74rem' }}>
              <th style={{ width: 40 }}></th>
              <th style={{ textAlign: 'left', padding: '8px 4px', width: 92 }}>Tarih</th>
              <th style={{ textAlign: 'left', padding: '8px 4px', width: 140 }}>Fiş No</th>
              <th style={{ textAlign: 'left', padding: '8px 4px' }}>Açıklama</th>
              <th style={{ textAlign: 'left', padding: '8px 4px', width: 110 }}>Ödeme</th>
              <th style={{ textAlign: 'right', padding: '8px 4px', width: 100 }}>Net</th>
              <th style={{ textAlign: 'right', padding: '8px 4px', width: 70 }}>KDV%</th>
              <th style={{ textAlign: 'right', padding: '8px 4px', width: 120 }}>Toplam</th>
            </tr>
          </thead>
          <tbody>
            {movements.map(m => (
              <tr key={m.id} style={{
                borderBottom: '1px solid var(--border-secondary)',
                opacity: m.status === 'cancelled' ? 0.5 : 1,
              }}>
                <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                  <button onClick={() => onOpenVoucher && onOpenVoucher(m.voucher_id)} title="Gider fişini aç"
                    style={{
                      background: 'rgba(124,58,237,0.10)', border: '1px solid rgba(124,58,237,0.25)',
                      color: '#7c3aed', padding: '4px 8px', borderRadius: 6, cursor: 'pointer',
                      fontSize: '0.78rem',
                    }}>📋</button>
                </td>
                <td style={{ padding: '6px 4px', textDecoration: m.status === 'cancelled' ? 'line-through' : 'none' }}>{fmtDate(m.voucher_date)}</td>
                <td style={{ padding: '6px 4px', textDecoration: m.status === 'cancelled' ? 'line-through' : 'none' }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{m.voucher_no}</span>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>{DOC_LABELS[m.document_type] || m.document_type}</div>
                </td>
                <td style={{ padding: '6px 4px', textDecoration: m.status === 'cancelled' ? 'line-through' : 'none' }}>
                  {m.line_description || m.voucher_description || '-'}
                  {m.account_title && <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>{m.account_title}</div>}
                </td>
                <td style={{ padding: '6px 4px', fontSize: '0.78rem', textDecoration: m.status === 'cancelled' ? 'line-through' : 'none' }}>{PAY_LABELS[m.payment_method] || m.payment_method}</td>
                <td style={{ padding: '6px 4px', textAlign: 'right', fontFamily: 'monospace', textDecoration: m.status === 'cancelled' ? 'line-through' : 'none' }}>{fmt(m.net_amount)}</td>
                <td style={{ padding: '6px 4px', textAlign: 'right', textDecoration: m.status === 'cancelled' ? 'line-through' : 'none' }}>%{m.vat_rate}</td>
                <td style={{ padding: '6px 4px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, textDecoration: m.status === 'cancelled' ? 'line-through' : 'none' }}>{fmt(m.gross_amount)} {m.currency_code}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12,
        padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.88rem',
      }}>
        <span style={{ color: 'var(--text-tertiary)' }}>Toplam (iptal hariç):</span>
        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--danger)' }}>{fmt(total)} TRY</span>
      </div>
    </div>
  );
}
